import { color, mono } from '../tokens';
import { type FPStep, type FPBlock } from '../fixtures';
import { useSynth, useSynthNav } from '../SynthesisContext';
import { asText, clipWords } from '../asText';

const LIST_FIELDS: Array<keyof FPBlock> = ['items', 'axioms', 'reasons', 'drops', 'bets', 'arch', 'apps'];

const FP_FRAME = [
  { n: 1, title: 'ASSESS THE CURRENT REALITY', subtitle: 'observe · categorise · simplify' },
  { n: 2, title: 'IDENTIFY THE FIRST PRINCIPLES', subtitle: 'recognise · understand · transcend' },
  { n: 3, title: 'SHAPE A NEW REALITY', subtitle: 'combinations · reintegrate · apply' },
] as const;

function themeRanksFromText(text: string): number[] {
  const m = /themes?\s+([\d,\sand]+)/i.exec(text);
  if (!m) return [];
  return m[1].split(/[,\s]+/).map((s) => parseInt(s.replace(/^and$/i, ''), 10)).filter((n) => Number.isFinite(n) && n > 0);
}

function ThemeChips({ ranks, onOpen }: { ranks: number[]; onOpen: (rank: number) => void }) {
  const { themes } = useSynth();
  if (!ranks.length) return null;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {ranks.map((r) => {
        const t = themes.find((th) => th.rank === r);
        return (
          <button
            key={r}
            onClick={() => onOpen(r)}
            title={t ? asText(t.summary || t.name) : undefined}
            style={{
              fontSize: 11, fontFamily: mono, color: color.purpleDeep, background: color.pillActiveBg,
              border: `1px solid ${color.chipBorder}`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
            }}
          >
            T{String(r).padStart(2, '0')}{t ? ` · ${clipWords(t.name, 24)}` : ''}
          </button>
        );
      })}
    </div>
  );
}

function BlockBody({ b, onOpenTheme, onOpenInsights }: { b: FPBlock; onOpenTheme: (rank: number) => void; onOpenInsights: () => void }) {
  const ranks = [
    ...(b.themes || []),
    ...themeRanksFromText(asText(b.body)),
    ...themeRanksFromText(asText(b.rationale)),
    ...themeRanksFromText(asText(b.whyMatters)),
  ].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <>
      {b.body && (
        <p style={{ margin: '0 0 8px', fontSize: 12.5, color: color.text, lineHeight: 1.55 }}>{asText(b.body)}</p>
      )}

      {b.stats && b.stats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          {b.stats.map((s, i) => (
            <div key={i} style={{ background: color.surfaceAlt, borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: mono, color: color.ink }}>{asText(s.v)}</div>
              <div style={{ fontSize: 11, color: color.textMuted, marginTop: 2 }}>{asText(s.k)}</div>
            </div>
          ))}
        </div>
      )}

      {LIST_FIELDS.map((f) => {
        // A list item is usually a string, but some blocks (e.g. "Categorise") carry
        // { name, count, muted } objects. Render either shape — never pass a raw object to React.
        const arr = b[f] as Array<string | { name?: string; count?: number; muted?: boolean }> | undefined;
        if (!arr || !arr.length) return null;
        return (
          <ul key={String(f)} style={{ margin: '0 0 8px', paddingLeft: 16 }}>
            {arr.map((it, i) => {
              const obj = it && typeof it === 'object' ? it : null;
              const label = obj
                ? `${obj.name ?? ''}${obj.count != null ? ` · ${obj.count}` : ''}`
                : String(it);
              return (
                <li key={i} style={{ fontSize: 12.5, color: obj?.muted ? color.textFaint : color.text, lineHeight: 1.5 }}>{label}</li>
              );
            })}
          </ul>
        );
      })}

      {b.note && (
        <p style={{ margin: '0 0 6px', fontSize: 11.5, color: color.textFaint, fontStyle: 'italic' }}>{asText(b.note)}</p>
      )}

      {(b.rationale || b.whyMatters) && (
        <div style={{ marginTop: 8, borderTop: `1px solid ${color.hairline}`, paddingTop: 8 }}>
          {b.rationale && (
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>RATIONALE </span>
              <span style={{ fontSize: 12, color: color.textMuted }}>{asText(b.rationale)}</span>
            </div>
          )}
          {b.whyMatters && (
            <div>
              <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>WHY IT MATTERS </span>
              <span style={{ fontSize: 12, color: color.textMuted }}>{asText(b.whyMatters)}</span>
            </div>
          )}
        </div>
      )}

      <ThemeChips ranks={ranks} onOpen={onOpenTheme} />
      {(b.opportunities || []).length > 0 && (
        <button
          onClick={onOpenInsights}
          style={{ marginTop: 8, fontSize: 11, color: color.purple, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          → {b.opportunities!.length} linked opportunit{b.opportunities!.length === 1 ? 'y' : 'ies'} in Actions & Opportunities
        </button>
      )}
    </>
  );
}

function StepCard({ step, index, onOpenTheme, onOpenInsights }: {
  step: FPStep; index: number; onOpenTheme: (rank: number) => void; onOpenInsights: () => void;
}) {
  const frame = FP_FRAME[index] || FP_FRAME[0];
  const n = step.n || frame.n;
  const title = asText(step.title) || frame.title;
  const subtitle = asText(step.subtitle) || frame.subtitle;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <span style={{
          width: 26, height: 26, borderRadius: 8, background: color.iconGrad, color: color.purpleDeep,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, fontFamily: mono, flexShrink: 0,
        }}>{n}</span>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.purple }}>STEP {n}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: color.ink }}>{title}</div>
          <div style={{ fontSize: 12.5, color: color.textMuted }}>{subtitle}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {step.blocks.map((b, i) => (
          <div key={i} style={{
            border: `1px solid ${color.hairline}`, borderRadius: 12, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple, marginBottom: 8 }}>
              {asText(b.label)}
            </div>
            <BlockBody b={b} onOpenTheme={onOpenTheme} onOpenInsights={onOpenInsights} />
          </div>
        ))}
      </div>
    </div>
  );
}

function FrameStrip() {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 44px 1fr 44px 1fr', gap: 12, alignItems: 'center',
      padding: '18px 20px', background: color.pillActiveBg, border: `1px solid ${color.chipBorder}`,
      borderRadius: 12, marginBottom: 28,
    }}>
      {FP_FRAME.map((s, i) => (
        <span key={s.n} style={{ display: 'contents' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 36, height: 36, borderRadius: '50%', background: '#DDD6FE', color: color.purpleDeep,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, flexShrink: 0,
            }}>{s.n}</span>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: color.purpleDeep }}>{s.title}</div>
              <div style={{ fontSize: 12, color: color.purpleDeep, marginTop: 2 }}>{s.subtitle}</div>
            </div>
          </div>
          {i < 2 && (
            <svg width="44" height="40" viewBox="0 -4 44 44" fill="none">
              <path d="M4 24 Q 22 10, 40 24" stroke="#7C3AED" strokeWidth="2" fill="none" strokeLinecap="round" />
              <path d="M34 18 L 40 24 L 34 30" stroke="#7C3AED" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      ))}
    </div>
  );
}

export default function FirstPrinciples() {
  const { firstPrinciples } = useSynth();
  const { setActive, setExpandedThemeRank } = useSynthNav();
  return (
    <div>
      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>FIRST PRINCIPLES</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            Reasoning from first principles
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
            Fully generative — no extraction provenance. A human stays between the model and the client.
          </p>
        </div>
        <button style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
          padding: '8px 12px', borderRadius: 8, background: color.surface, color: color.text,
          border: `1px solid ${color.hairline}`,
        }}>
          Promote to strategy brief
        </button>
      </div>
      <div style={{
        margin: '14px 0 20px', fontSize: 12, color: '#B45309',
        background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 12px',
      }}>
        ⚠ Draft — promotion opens an editable draft for review. Never a one-click send to the client.
      </div>

      <FrameStrip />

      {firstPrinciples.map((step, i) => (
        <StepCard
          key={step.n || i}
          step={step}
          index={i}
          onOpenTheme={(rank) => { setExpandedThemeRank(rank); setActive('key-themes'); }}
          onOpenInsights={() => setActive('insights')}
        />
      ))}
    </div>
  );
}
